import cx from 'classnames';
import { EventDateLocation } from 'govbr.frontend.policy/components/BlockTemplates/Listing/Specific/Views/ListingParts';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

/**
 * ========================================
 * MENSAGENS INTERNACIONALIZADAS
 * ========================================
 */
const messages = defineMessages({
  dateTime: {
    id: 'Date and Time',
    defaultMessage: 'Date and Time',
  },
  location: {
    id: 'Location',
    defaultMessage: 'Location',
  },
  attendees: {
    id: 'Participants',
    defaultMessage: 'Participants',
  },
  contact: {
    id: 'Contact Information',
    defaultMessage: 'Contact Information',
  },
  contactName: {
    id: 'Contact Name',
    defaultMessage: 'Contact Name',
  },
  contactEmail: {
    id: 'Email',
    defaultMessage: 'Email',
  },
  contactPhone: {
    id: 'Phone',
    defaultMessage: 'Phone',
  },
  moreInfo: {
    id: 'More information',
    defaultMessage: 'More information',
  },
  eventLink: {
    id: 'Event Link',
    defaultMessage: 'Event Link',
  },
  visitWebsite: {
    id: 'Visit external website',
    defaultMessage: 'Visit external website',
  },
  noInfo: {
    id: 'This event has no registered information',
    defaultMessage: 'This event has no registered information',
  },
  fillDatetime: {
    id: 'Fill in the event date and time in the Event tab',
    defaultMessage: 'Fill in the event date and time in the Event tab',
  },
  fillLocation: {
    id: 'Fill in the event location in the Event tab',
    defaultMessage: 'Fill in the event location in the Event tab',
  },
  fillAttendees: {
    id: 'Fill in the participants in the Event tab',
    defaultMessage: 'Fill in the participants in the Event tab',
  },
  fillContact: {
    id: 'Fill in the contact information in the Event tab',
    defaultMessage: 'Fill in the contact information in the Event tab',
  },
  fillUrl: {
    id: 'Fill in the event URL in the Event tab',
    defaultMessage: 'Fill in the event URL in the Event tab',
  },
  customSectionPlaceholder: {
    id: 'Fill in the content in the block tab',
    defaultMessage: 'Fill in the content in the block tab',
  },
  programming: {
    id: 'Programming',
    defaultMessage: 'Programming',
  },
  usefulInfo: {
    id: 'Useful Information',
    defaultMessage: 'Useful Information',
  },
});

/**
 * ========================================
 * COMPONENTES AUXILIARES (Helper Components)
 * ========================================
 */

/**
 * Componente base para renderizar um item de informação com ícone e conteúdo
 */
const InfoItem = ({ icon, label, children }) => (
  <div className="mb-3">
    <div className="d-flex align-items-center mb-2">
      <i
        className={cx(icon, 'mr-2 text-primary-default')}
        aria-hidden="true"
      ></i>
      <span className="text-up-01 text-semi-bold text-gray-70">{label}</span>
    </div>
    <div className="pl-5x">{children}</div>
  </div>
);

/**
 * Placeholder para campos vazios em modo edição
 */
const PlaceholderText = ({ message }) => (
  <p className="m-0 text-up-01 text-gray-60 font-italic">{message}</p>
);

/**
 * ========================================
 * SEÇÕES DE INFORMAÇÃO DO EVENTO
 * ========================================
 */

/**
 * Renderiza a seção de Data e Hora
 */
const DateTimeSection = ({ start, eventData, intl }) => (
  <InfoItem
    icon="far fa-calendar"
    label={intl.formatMessage(messages.dateTime)}
  >
    {start ? (
      <EventDateLocation
        item={eventData}
        dateFormat="DD/MM/YYYY"
        timeFormat="HH:mm"
        onlyDate
      />
    ) : (
      <PlaceholderText message={intl.formatMessage(messages.fillDatetime)} />
    )}
  </InfoItem>
);

/**
 * Renderiza a seção de Localização
 */
const LocationSection = ({ location, intl }) => (
  <InfoItem
    icon="fas fa-map-marker-alt"
    label={intl.formatMessage(messages.location)}
  >
    {location ? (
      <p className="m-0 text-up-01">{location}</p>
    ) : (
      <PlaceholderText message={intl.formatMessage(messages.fillLocation)} />
    )}
  </InfoItem>
);

/**
 * ========================================
 * SEÇÃO DE PARTICIPANTES
 * ========================================
 */

/**
 * Renderiza a lista de participantes
 */
const AttendeesList = ({ attendees, intl }) => (
  <div>
    {attendees && attendees.length > 0 ? (
      <ul className="m-0 pl-3">
        {attendees.map((attendee, idx) => (
          <li key={idx} className="text-up-01 mb-1">
            {attendee}
          </li>
        ))}
      </ul>
    ) : (
      <PlaceholderText message={intl.formatMessage(messages.fillAttendees)} />
    )}
  </div>
);

/**
 * Renderiza a seção de Participantes
 */
const AttendeesSection = ({ attendees, intl }) => (
  <InfoItem icon="fas fa-users" label={intl.formatMessage(messages.attendees)}>
    <AttendeesList attendees={attendees} intl={intl} />
  </InfoItem>
);

/**
 * ========================================
 * SEÇÃO DE CONTATO
 * ========================================
 */

/**
 * Renderiza os detalhes de um contato (nome, email, telefone)
 */
const ContactDetails = ({
  contact_name,
  contact_email,
  contact_phone,
  intl,
}) => (
  <div>
    {/* Nome do Contato */}
    {contact_name && (
      <div className="mb-2">
        <span className="text-up-01 text-semi-bold text-gray-70">
          {intl.formatMessage(messages.contactName)}:
        </span>
        <p className="m-0 mt-1 text-up-01">{contact_name}</p>
      </div>
    )}

    {/* Email do Contato */}
    {contact_email && (
      <div className="mb-2">
        <span className="text-up-01 text-semi-bold text-gray-70">
          {intl.formatMessage(messages.contactEmail)}:
        </span>
        <p className="m-0 mt-1">
          <a href={`mailto:${contact_email}`} className="text-interactive">
            {contact_email}
          </a>
        </p>
      </div>
    )}

    {/* Telefone do Contato */}
    {contact_phone && (
      <div className="mb-0">
        <span className="text-up-01 text-semi-bold text-gray-70">
          {intl.formatMessage(messages.contactPhone)}:
        </span>
        <p className="m-0 mt-1">
          <a href={`tel:${contact_phone}`} className="text-interactive">
            {contact_phone}
          </a>
        </p>
      </div>
    )}
  </div>
);

/**
 * Renderiza a seção de Contato
 */
const ContactSection = (props) => {
  const { contact_name, contact_email, contact_phone, intl } = props;
  const hasContactInfo = contact_name || contact_email || contact_phone;

  return (
    <InfoItem
      icon="fas fa-envelope"
      label={intl.formatMessage(messages.contact)}
    >
      {hasContactInfo ? (
        <ContactDetails {...props} />
      ) : (
        <PlaceholderText message={intl.formatMessage(messages.fillContact)} />
      )}
    </InfoItem>
  );
};

/**
 * ========================================
 * SEÇÕES CUSTOMIZADAS DO BLOCO
 * ========================================
 */

/**
 * Renderiza uma seção customizada do bloco
 */
const CustomSection = ({ section, index, intl }) => (
  <div className="col-12 col-md-6 pl-0">
    <InfoItem
      icon={section.icon || 'fas fa-info-circle'}
      label={section.title || `Seção ${index + 1}`}
    >
      {section.content ? (
        <div
          className="text-up-01"
          dangerouslySetInnerHTML={{ __html: section.content }}
        />
      ) : (
        <PlaceholderText
          message={intl.formatMessage(messages.customSectionPlaceholder)}
        />
      )}
    </InfoItem>
  </div>
);

/**
 * Renderiza exemplo de seção padrão (usado em modo edição)
 */
const DefaultExampleSection = ({ icon, label, intl }) => (
  <div className="col-12 col-md-6 pl-0">
    <InfoItem icon={icon} label={label}>
      <PlaceholderText
        message={intl.formatMessage(messages.customSectionPlaceholder)}
      />
    </InfoItem>
  </div>
);

/**
 * Renderiza seções customizadas do bloco
 * - Se houver seções, exibe elas
 * - Se não houver e estiver em edit mode E não tiver sido editado antes, exibe exemplos padrão
 */
const CustomSectionsContainer = ({
  customSections,
  isEditMode,
  hasBeenEdited,
  intl,
}) => {
  const hasCustomSections = customSections && customSections.length > 0;

  // Se há seções customizadas, renderizar elas
  if (hasCustomSections) {
    return customSections.map((section, idx) => (
      <CustomSection key={idx} section={section} index={idx} intl={intl} />
    ));
  }

  // Se em modo edit, sem seções E nunca foi editado, exibir exemplos padrão
  if (isEditMode && !hasBeenEdited) {
    return (
      <>
        {/* Exemplo 1: Programming */}
        <DefaultExampleSection
          icon="fas fa-calendar"
          label={intl.formatMessage(messages.programming)}
          intl={intl}
        />

        {/* Exemplo 2: Useful Information */}
        <DefaultExampleSection
          icon="fas fa-info-circle"
          label={intl.formatMessage(messages.usefulInfo)}
          intl={intl}
        />

        {/* Exemplo 3: More Information */}
        <DefaultExampleSection
          icon="fas fa-ellipsis-h"
          label={intl.formatMessage(messages.moreInfo)}
          intl={intl}
        />
      </>
    );
  }

  return null;
};

/**
 * ========================================
 * COMPONENTE PRINCIPAL
 * ========================================
 */

/**
 * Componente principal de exibição de informações do evento
 *
 * Props:
 *   - content: Dados do evento (vindo do context da página Event)
 *   - data: Dados do bloco (seções customizadas)
 *   - isEditMode: boolean indicando se está em modo edição
 */
const EventInfoBody = (props) => {
  const intl = useIntl();
  const { content, data, isEditMode } = props;

  // Suporta tanto content (do context da página Event) quanto data (do bloco)
  const eventData = content || {};
  const blockData = data || {};

  // Extrai dados do evento
  const {
    start,
    location,
    attendees = [],
    contact_name,
    contact_email,
    contact_phone,
    event_url,
  } = eventData;

  // Extrai seções customizadas do bloco
  const { customSections = [], hasBeenEdited = false } = blockData;

  /**
   * Verifica se há alguma informação de evento para exibir
   */
  const hasEventInfo =
    start ||
    location ||
    (attendees && attendees.length > 0) ||
    contact_name ||
    contact_email ||
    contact_phone ||
    event_url;

  /**
   * Verifica se há seções customizadas
   */
  const hasCustomSections = customSections && customSections.length > 0;

  /**
   * Em modo view (não edição), se não há informações, não renderizar nada
   */
  if (!hasEventInfo && !hasCustomSections && !isEditMode) {
    return null;
  }

  return (
    <div>
      {/* ===== PRIMEIRA LINHA: Data/Hora + Localização ===== */}
      <div className="row mb-3">
        {(start || isEditMode) && (
          <div className="col-12 col-md-6 pl-0">
            <DateTimeSection start={start} eventData={eventData} intl={intl} />
          </div>
        )}

        {(location || isEditMode) && (
          <div className="col-12 col-md-6 pl-0">
            <LocationSection location={location} intl={intl} />
          </div>
        )}
      </div>

      {/* ===== SEGUNDA LINHA: Participantes + Contato ===== */}
      <div className="row mb-3">
        {((attendees && attendees.length > 0) || isEditMode) && (
          <div className="col-12 col-md-6 pl-0">
            <AttendeesSection attendees={attendees} intl={intl} />
          </div>
        )}

        {(contact_name || contact_email || contact_phone || isEditMode) && (
          <div className="col-12 col-md-6 pl-0">
            <ContactSection
              contact_name={contact_name}
              contact_email={contact_email}
              contact_phone={contact_phone}
              intl={intl}
            />
          </div>
        )}

        {/* Link para o Evento */}
        {(event_url || isEditMode) && (
          <div className="col-12 col-md-6 pl-0">
            <InfoItem
              icon="fas fa-external-link-alt"
              label={intl.formatMessage(messages.eventLink)}
            >
              {event_url ? (
                <a href={event_url} target="_blank" rel="noopener noreferrer">
                  {intl.formatMessage(messages.visitWebsite)}
                </a>
              ) : (
                <PlaceholderText
                  message={intl.formatMessage(messages.fillUrl)}
                />
              )}
            </InfoItem>
          </div>
        )}

        {/* ===== Seções Customizadas ===== */}
        {(hasCustomSections || (isEditMode && !hasBeenEdited)) && (
          <CustomSectionsContainer
            customSections={customSections}
            isEditMode={isEditMode}
            hasBeenEdited={hasBeenEdited}
            intl={intl}
          />
        )}
      </div>
    </div>
  );
};

EventInfoBody.propTypes = {
  content: PropTypes.object,
  data: PropTypes.object,
  isEditMode: PropTypes.bool,
};

export default EventInfoBody;
